import queryString from 'query-string'
import { isEmpty, isNil, orderBy, zipWith, merge } from 'lodash'

import { toast } from '@/components/toasts/toast.helpers'
import { RECENT_QUESTIONS_DYNAMIC_INSIGHTS_FEATURE_NAME } from '@/utils/constants'
import { formatTrendsFilters, hasActiveTrendFilters } from '@/views/Trends/trendsFilters.helpers'
import { fetchingAPI, apiService } from '../../api'
import * as actions from './insightEvents.redux'
import * as dynamicInsightsActions from './dynamicInsightEvents.redux'
import * as trendsActions from './trends.redux'

import { checkOrderDecklist, collectTrendCallData } from './insights.helpers'
import { fetchRecentQuestions } from '../recentQuestions/recentQustions.actions'

export const fetchInsightDetails =
  (cid, organizationId, categoryName, deckName, decklist) => async (dispatch) => {
    try {
      const queryParam = `?${queryString.stringify({
        cid,
        org_id: organizationId,
        deck_cat: categoryName,
        deck_name: deckName,
      })}`
      const insights = await fetchingAPI(
        `${apiService.insights}/api/recommendations/decklist${queryParam}`,
        'GET',
        dispatch
      )
      dispatch(actions.setInsightDetails(insights))

      const [result, orderSuggestion] = checkOrderDecklist(insights, decklist)
      dispatch(actions.setInsightsReorderRecommended(!result))
      if (!result) {
        dispatch(actions.setInsightsSuggestedOrder(orderSuggestion))
      }
    } catch (err) {
      console.error(err)
    }
  }

export const saveInsightEvent = (body) => async (dispatch) => {
  try {
    await fetchingAPI(`${apiService.insights}/api/events`, 'POST', dispatch, JSON.stringify(body))
  } catch (err) {
    console.error(err)
  }
}

const processAnchors = (lineJson) => {
  if ('anchors' in lineJson) {
    // save anchors into localstore because opening a new tab resets redux
    // need to stringify first because JavaScript automatically converts the object to a string
    // which is not what we want as it looks like this: "[object Object]"
    const dynamicInsightAnchors = localStorage.getItem('dynamicInsightsAnchors')
      ? JSON.parse(localStorage.getItem('dynamicInsightsAnchors'))
      : {}
    // set new anchors
    dynamicInsightAnchors[lineJson.session_id] = {
      anchors: lineJson.anchors,
      timestamp: Date.now(),
    }

    // Loop through all older dynamic insight anchors and delete all the anchors older than 1 day
    const ONE_DAY_MS = 24 * 60 * 60 * 1000
    const now = Date.now()
    for (const session_id in dynamicInsightAnchors) {
      if (Object.prototype.hasOwnProperty.call(dynamicInsightAnchors, session_id)) {
        const entry = dynamicInsightAnchors[session_id]
        if (!entry.timestamp || now - entry.timestamp > ONE_DAY_MS) {
          delete dynamicInsightAnchors[session_id]
        }
      }
    }

    localStorage.setItem('dynamicInsightsAnchors', JSON.stringify(dynamicInsightAnchors))
  }
}

export const publishInsightEvents = (events, id) => async (dispatch) => {
  try {
    await Promise.all(
      events.map((item) => {
        const saveEventPayload = { ...item, action_taken: 'save', new_config_id: id }

        return fetchingAPI(
          `${apiService.insights}/api/events`,
          'POST',
          dispatch,
          JSON.stringify(saveEventPayload)
        )
      })
    )
    dispatch(actions.clearInsightEvents())
  } catch (err) {
    console.error(err)
  }
}

export const NO_CALLS_MATCHED_ERROR =
  'No conversations matched your filters. Try widening your date range or removing filters, then ask again.'

export const STREAM_INTERRUPTED_ERROR =
  'The answer stopped part way through. Please ask your question again.'

// Sampling happens before the stream opens, so this stage is dispatched client side to match
// the shape of the stages the dynsight API streams for the work that follows
export const SAMPLING_CALLS_STAGE = 'Stage 1: Sampling Calls'

export const fetchDynamicInsights =
  (body, username, token, organizationid, abortSignal, signalDone) => async (dispatch) => {
    dispatch(dynamicInsightsActions.setIsLoading(true))
    dispatch(
      dynamicInsightsActions.setDyanmicInsightEvents(
        JSON.stringify({ type: 'progress', stage: SAMPLING_CALLS_STAGE })
      )
    )

    let callIds = []
    try {
      const { question, ...callFilters } = body

      const callPayload = {
        ...callFilters,
        num_call_samples: 200,
        allow_unlimited_date_range: true,
      }

      const { calls } = await fetchingAPI(
        `${apiService.reporting}/api/calls?no_timeout=true`,
        'POST',
        dispatch,
        JSON.stringify(callPayload),
        undefined,
        undefined,
        undefined,
        abortSignal
      )
      // Map and filter out both null and undefined call IDs (intentional !=)
      callIds = calls.map((call) => call.call_id).filter((id) => id != null)
    } catch (err) {
      signalDone()
      if (err?.name === 'AbortError') {
        dispatch(dynamicInsightsActions.resetDynamicInsightEvents())
        return
      }
      console.error('Failed to fetch calls:', err)
      toast.error('Failed to retrieve calls for dynamic insights', { cause: err })
      dispatch(dynamicInsightsActions.resetDynamicInsightEvents())
      return
    }

    // Without calls there is nothing for the model to read, so skip the request and tell the
    // user their filters are the problem rather than letting an empty answer imply otherwise
    if (isEmpty(callIds)) {
      signalDone()
      dispatch(
        dynamicInsightsActions.setDyanmicInsightEvents(
          JSON.stringify({ type: 'result', err: NO_CALLS_MATCHED_ERROR, categories: [] })
        )
      )
      dispatch(dynamicInsightsActions.setIsLoading(false))
      return
    }

    const bodyWithCallIds = {
      ...body,
      call_ids: callIds,
    }

    let response
    try {
      response = await fetch(`${apiService.summary}/dynsight`, {
        method: 'POST',
        body: JSON.stringify(bodyWithCallIds),
        headers: {
          'Content-Type': 'application/json',
          'auth-type': 'web',
          organizationid,
          username,
          token,
        },
        signal: abortSignal,
      })
    } catch (err) {
      signalDone()
      if (err?.name === 'AbortError') {
        dispatch(dynamicInsightsActions.resetDynamicInsightEvents())
        return
      }
      console.error('Failed to start dynamic insights:', err)
      toast.error('Failed to retrieve dynamic insights', { cause: err })
      dispatch(dynamicInsightsActions.resetDynamicInsightEvents())
      return
    }

    if (!response.ok) {
      signalDone()
      let errorMessage = 'Failed to retrieve dynamic insights'
      try {
        const errorData = await response.json()
        if (errorData.detail) {
          errorMessage = errorData.detail
        }
      } catch {
        // use the default message
      }
      toast.error(errorMessage)
      dispatch(dynamicInsightsActions.resetDynamicInsightEvents())
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        // Decode the incoming chunk and append it to the buffer
        buffer += decoder.decode(value, { stream: true })

        // Process complete lines
        let newlineIndex
        // eslint-disable-next-line no-cond-assign
        while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
          const line = buffer.slice(0, newlineIndex).trim()
          buffer = buffer.slice(newlineIndex + 1) // Remove processed line

          if (line) {
            try {
              dispatch(dynamicInsightsActions.setDyanmicInsightEvents(line))
              const lineJson = JSON.parse(line)
              processAnchors(lineJson)
            } catch (err) {
              console.error('JSON parse error:', err, line)
            }
          }
        }
      }
      // An abort landing on the final chunk exits the loop through done rather than rejecting,
      // so check it here to keep Stop from holding on to the answer it was meant to drop
      if (abortSignal?.aborted) {
        const abortError = new Error('The operation was aborted.')
        abortError.name = 'AbortError'
        throw abortError
      }
      signalDone()
    } catch (err) {
      signalDone()
      if (err?.name === 'AbortError') {
        // The user hit Stop, so drop the partial answer and return the page to its idle state
        dispatch(dynamicInsightsActions.resetDynamicInsightEvents())
      } else {
        console.error('Stream read error:', err)
        dispatch(
          dynamicInsightsActions.setDyanmicInsightEvents(
            JSON.stringify({ type: 'result', err: STREAM_INTERRUPTED_ERROR, categories: [] })
          )
        )
      }
    } finally {
      dispatch(fetchRecentQuestions(RECENT_QUESTIONS_DYNAMIC_INSIGHTS_FEATURE_NAME))
      dispatch(dynamicInsightsActions.setIsLoading(false))
    }
  }

export const createTrend = (taxonomyBody, insightBody) => async (dispatch) => {
  dispatch(trendsActions.setLoading({ trend: true }))

  try {
    const taxonomy = await fetchingAPI(
      `${apiService.summary}/taxonomies`,
      'POST',
      dispatch,
      JSON.stringify(taxonomyBody)
    )

    dispatch(trendsActions.setTaxonomy(taxonomy.taxonomy_uuid))

    insightBody.taxonomy_uuid = taxonomy.taxonomy_uuid

    const response = await fetchingAPI(
      `${apiService.summary}/tracked-insights`,
      'POST',
      dispatch,
      JSON.stringify(insightBody)
    )

    toast.success('Trend created successfully')

    dispatch(trendsActions.setTrend(response))
  } catch (err) {
    toast.error('Failed to create trend', { cause: err })
  } finally {
    dispatch(trendsActions.setLoading({ trend: false }))
  }
}

export const fetchTrends =
  (orgId, startDate, endDate, filters = null, playbookData = null) =>
  async (dispatch) => {
    const queryParams = queryString.stringify({ start_time: startDate, end_time: endDate })
    // With no filters set the GET stays in play, so an unfiltered board keeps
    // the exact request it has always made.
    // Only a narrowing choice makes this a filtered POST; scope alone keeps the
    // plain GET the board has always issued.
    const body =
      isNil(filters) || !hasActiveTrendFilters(filters)
        ? null
        : formatTrendsFilters(filters, playbookData, startDate, endDate)

    try {
      dispatch(trendsActions.setLoading({ trends: true }))
      // get all trends for an org
      const trends = await fetchingAPI(
        `${apiService.summary}/tracked-insights?requested_organization_id=${orgId}`,
        'GET',
        dispatch
      )

      const sortedTrendsByOrder = orderBy(trends, ['order'])

      // map all data for each trend
      const trendsData = await Promise.all(
        sortedTrendsByOrder.map(async (trend) => {
          if (isNil(body)) {
            return fetchingAPI(
              `${apiService.summary}/tracked-insights/${trend.id}/results?${queryParams}`,
              'GET',
              dispatch
            )
          }
          return fetchingAPI(
            `${apiService.summary}/tracked-insights/${trend.id}/results`,
            'POST',
            dispatch,
            JSON.stringify(body)
          )
        })
      )

      // row by row merge
      const zippedTrends = zipWith(sortedTrendsByOrder, trendsData, merge)

      dispatch(trendsActions.setTrends(zippedTrends))
    } catch (err) {
      toast.error('Failed to fetch trends', { cause: err })
    } finally {
      dispatch(trendsActions.setLoading({ trends: false }))
    }
  }

/**
 * Reload the board from whatever the filter bar has committed.
 *
 * Reads the slice at dispatch time rather than closing over it, so a caller can
 * fire this immediately after a filter lands without racing the re-render —
 * mirrors `fetchCallsFromLatestFilters`.
 */
export const fetchTrendsFromLatestFilters = () => async (dispatch, getState) => {
  const { currentUser, callSearch } = getState()
  const { filters, data } = callSearch
  const orgId = filters.organizationId || currentUser.organizationid

  if (isEmpty(filters.startDate) || isEmpty(filters.endDate)) {
    return
  }

  await dispatch(fetchTrends(orgId, filters.startDate, filters.endDate, filters, data.playbookData))
}

export const fetchTrendCountForOrg = (orgId) => async (dispatch) => {
  try {
    const trendCount = await fetchingAPI(
      `${apiService.summary}/tracked-insights/count?requested_organization_id=${orgId}`,
      'GET',
      dispatch
    )

    dispatch(trendsActions.setTrendCount(trendCount))
  } catch (err) {
    toast.error('Failed to fetch trend count', { cause: err })
    console.error(err)
  }
}

export const deleteTrend = (insightId) => async (dispatch) => {
  try {
    await fetchingAPI(`${apiService.summary}/tracked-insights/${insightId}`, 'DELETE', dispatch)

    toast.success('Trend deleted successfully')
  } catch (err) {
    toast.error('Failed to delete trend', { cause: err })
    console.error(err)
  }
}

export const updateTrendName = (id, name) => async (dispatch) => {
  try {
    await fetchingAPI(
      `${apiService.summary}/tracked-insights/${id}`,
      'PATCH',
      dispatch,
      JSON.stringify({
        name,
      })
    )
    toast.success('Trend name updated successfully')
  } catch (err) {
    toast.error('Failed to update trend name', { cause: err })
    console.error(err)
  }
}

export const downloadTrendCallsCsv =
  (trend, startDate, endDate, abortController) => async (dispatch) => {
    const { callIds, callLabels } = collectTrendCallData(trend)
    if (isEmpty(callIds)) {
      toast.error('This trend has no calls to export')
      return
    }

    const toastId = toast.loading('Preparing your download...')

    // Summary columns are discovered server-side for call_ids exports; the
    // labels become the "Trend Series" column tagging each call's series.
    const body = {
      call_ids: callIds,
      organization_id: trend.organization_id,
      call_labels: callLabels,
      call_labels_header: 'Trend Series',
    }
    // Without call_types the export query only reads the calls CTE — a message
    // trend (email/sms/chat) would export zero rows, and the message columns
    // (Subject, Customer Email, Message Count) only render for these types.
    if (!isEmpty(trend.call_filters?.call_types)) {
      body.call_types = trend.call_filters.call_types
    }

    const signal = abortController?.signal
    try {
      // handleResponse resolves the raw body stream for text/csv responses;
      // isSilent suppresses its own 500 toast (we surface errors on toastId).
      const responseBody = await fetchingAPI(
        `${apiService.reporting}/api/calls/csv`,
        'POST',
        dispatch,
        JSON.stringify(body),
        null,
        { isSilent: true },
        undefined,
        signal
      )

      const reader = responseBody.getReader()
      const cancelOnAbort = () => reader.cancel()
      signal?.addEventListener('abort', cancelOnAbort)
      const chunks = []
      let bytesReceived = 0
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read()
        if (done) {
          break
        }
        chunks.push(value)
        bytesReceived += value.length
        toast.loading(`Downloading... ${(bytesReceived / (1024 * 1024)).toFixed(1)} MB received`, {
          id: toastId,
        })
      }
      signal?.removeEventListener('abort', cancelOnAbort)
      if (signal?.aborted) {
        const abortError = new Error('The operation was aborted.')
        abortError.name = 'AbortError'
        throw abortError
      }

      const blob = new Blob(chunks, { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      const trendName = trend.name.replace(/[\\/:*?"<>|]/g, '-')
      link.href = url
      link.download = `Balto Trend Export - ${trendName} ${startDate.slice(0, 10)} - ${endDate.slice(0, 10)}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      toast.success('Download complete.', { id: toastId })
    } catch (err) {
      if (err?.name === 'AbortError') {
        toast.dismiss(toastId)
        return
      }
      const backendMessage =
        (typeof err?.detail === 'string' && err.detail) ||
        (err?.status && typeof err?.message === 'string' && err.message)
      toast.error(backendMessage || 'Failed to export trend calls', { id: toastId, cause: err })
      console.error(err)
    }
  }

export const fetchEndingSentiment = (organizationId, startDate, endDate) => async (dispatch) => {
  dispatch(trendsActions.setLoading({ endingSentiment: true }))

  try {
    const response = await fetchingAPI(
      `${apiService.reporting}/api/sentiment/trend?requested_organization_id=${organizationId}&start_date=${startDate}&end_date=${endDate}`
    )

    dispatch(trendsActions.setEndingSentiment(response))
  } catch (err) {
    console.error(err)
  } finally {
    dispatch(trendsActions.setLoading({ endingSentiment: false }))
  }
}
